// External modules
import { useMemo } from 'react';

// Internal Modules
import { getContent, isUserActionCommit } from '@/lib';
import type { IconName } from '@/lib/icons';

type ContentResponse = ReturnType<typeof getContent>;

interface SpotConfig {
  content: ContentResponse;
  iconName: IconName;
  bgColor?: string;
}

interface ContentObject {
  primaryButton: ContentResponse;
  secondaryButton: ContentResponse;
  secondaryMsg?: ContentResponse;
  spots: SpotConfig[];
  title: ContentResponse;
  subTitle?: ContentResponse;
  treatment: number | 'Control';
}

interface UseMemberMarchContentResponse {
  boldContent: boolean;
  columnStyles: boolean;
  contentObject: ContentObject;
  showSecondaryMsg: boolean;
  showSubtitle: boolean;
}

export const useMemberMatchContent = ({
  treatmentVariant,
}: {
  treatmentVariant: string;
}): UseMemberMarchContentResponse => {
  const isCommit = isUserActionCommit();
  const contentObject = useMemo<ContentObject>(() => {
    const showTransparentBgColor = Number(treatmentVariant) === 2;
    const bgColor = showTransparentBgColor ? 'transparent' : undefined;

    const commonSpots: SpotConfig[] = [
      {
        bgColor,
        content: getContent('valueProps.purchaseProtection'),
        iconName: 'SHIELD_LOCK',
      },
      {
        bgColor,
        content: getContent('valueProps.easyReturns'),
        iconName: 'TRACK_NEW',
      },
      {
        bgColor,
        content: getContent('valueProps.cashBackAndRewards'),
        iconName: 'TROPHY_NEW',
      },
    ];

    switch (Number(treatmentVariant)) {
      case 2:
        return {
          primaryButton: getContent('buttons.guest.base'),
          secondaryButton: getContent('buttons.skipAndContinueTwo'),
          secondaryMsg: getContent(
            'valueProps.memberBenefits.noNeedToLoginWithNoPeriod',
          ),
          spots: commonSpots,
          title: getContent(
            'valueProps.memberBenefits.addPurchaseToYourAccount',
          ),
          treatment: 2,
        };
      case 3:
        return {
          primaryButton: getContent('buttons.guest.yesAddPurchase'),
          secondaryButton: getContent('buttons.guest.noSkipThis'),
          secondaryMsg: getContent(
            'valueProps.memberBenefits.addPurchasePayPalAccount',
          ),
          spots: commonSpots,
          subTitle: getContent(
            'valueProps.memberBenefits.noNeedToLoginWithPeriod',
          ),
          title: getContent(
            'valueProps.memberBenefits.eligibleAccountBenefits',
          ),
          treatment: 3,
        };
      case 4:
        return {
          primaryButton: getContent('buttons.guest.base'),
          secondaryButton: getContent('buttons.skipAndContinueTwo'),
          secondaryMsg: getContent(
            'valueProps.memberBenefits.noNeedToLoginWithNoPeriod',
          ),
          spots: [
            {
              content: getContent('valueProps.purchaseProtection'),
              iconName: 'CHECK_MARK',
            },
            {
              content: getContent('valueProps.easyReturns'),
              iconName: 'CHECK_MARK',
            },
            {
              content: getContent('valueProps.cashBackAndRewards'),
              iconName: 'CHECK_MARK',
            },
          ],
          title: getContent(
            'valueProps.memberBenefits.addPurchaseToYourAccount',
          ),
          treatment: 4,
        };
      default:
        return {
          primaryButton: isCommit
            ? getContent('buttons.useMemberBenefitsWps')
            : getContent('buttons.useMemberBenefits'),
          secondaryButton: isCommit
            ? getContent('buttons.skipAndContinueWps')
            : getContent('buttons.skipAndContinue'),
          spots: [
            {
              content: getContent('valueProps.purchaseProtection'),
              iconName: 'SHIELD',
            },
            {
              content: getContent('valueProps.creditCardRewards'),
              iconName: 'TROPHY',
            },
            {
              content: getContent('valueProps.easyReturns'),
              iconName: 'TRACK',
            },
          ],
          title: getContent('messages.memberAsDefault.eligibleForBenefits'),
          treatment: 'Control',
        };
    }
  }, [treatmentVariant]);
  const columnStyles = contentObject.treatment === 4;
  const showSecondaryMsg = contentObject?.treatment !== 'Control';
  const showSubtitle = contentObject?.treatment === 3;
  const boldContent =
    contentObject?.treatment === 3 || contentObject?.treatment === 4;
  return {
    boldContent,
    columnStyles,
    contentObject,
    showSecondaryMsg,
    showSubtitle,
  };
};
